import { type ReactNode } from 'react';
import './WhatsAppPreview.css';

export interface WhatsAppPreviewButton {
  text: string;
  type?: string;
  url?: string;
  phone_number?: string;
}

export interface WhatsAppPreviewProps {
  /**
   * The header. `format` is TEXT, IMAGE, VIDEO or DOCUMENT; a media header has
   * no text and is drawn as a placeholder tile instead, because that is what
   * the handset shows above the message.
   */
  header?: { text?: string; format?: string; mediaUrl?: string } | null;
  body?: string | null;
  footer?: string | null;
  buttons?: WhatsAppPreviewButton[] | null;
  /**
   * Wraps each `{{n}}` placeholder so it reads as a placeholder rather than as
   * literal text the customer will see. Given the position, returns the node
   * to render in its place.
   */
  renderPlaceholder?: (position: string) => ReactNode;
  /** Shown in place of the body when there is none. */
  emptyBodyLabel?: ReactNode;
  /**
   * Draw the handset around the bubble: a chat header with the business name,
   * the message on the conversation ground, and a dead composer at the foot.
   *
   * A bubble on a white card reads as a quote from a document. On a handset it
   * reads as the message a customer will actually receive, which is the only
   * question a template preview is answering.
   */
  chrome?: boolean;
  /** Business name in the chat header. Only used with `chrome`. */
  businessName?: ReactNode;
  /** Timestamp under the message. Only used with `chrome`. */
  timestamp?: ReactNode;
  /** Placeholder text in the dead composer. Only used with `chrome`. */
  composerLabel?: string;
  className?: string;
}

/**
 * A WhatsApp message bubble, for previewing a template before it is sent.
 *
 * Promoted into the design system because it was duplicated across both
 * consoles with different inline styles and different hardcoded colour
 * fallbacks (`apps/tenant-ui/src/pages/templates/view[id]/components/OverviewTab.tsx`
 * and `apps/admin-ui/src/pages/templates/view[id]/components/TemplateBody.tsx`),
 * and it is the most "designed" surface in either app.
 *
 * Copy is passed in rather than hardcoded: both consuming apps are fully
 * translated and this component is not.
 */
export function WhatsAppPreview({
  header,
  body,
  footer,
  buttons,
  renderPlaceholder,
  emptyBodyLabel,
  chrome,
  businessName,
  timestamp,
  composerLabel,
  className
}: WhatsAppPreviewProps): ReactNode {
  const render = (text: string): ReactNode => {
    if (!renderPlaceholder) return text;
    // Split on the placeholder, keeping the captured position, so the caller
    // can decorate it without the component owning any styling opinion.
    const parts = text.split(/(\{\{\s*\d+\s*\}\})/g);
    return parts.map((part, i) => {
      const match = /^\{\{\s*(\d+)\s*\}\}$/.exec(part);
      return match ? <span key={i}>{renderPlaceholder(match[1])}</span> : <span key={i}>{part}</span>;
    });
  };

  const message = (
    <>
      <div className="rr-wa-preview__bubble">
        {isMedia(header?.format) ? (
          <div className={`rr-wa-preview__media rr-wa-preview__media--${header!.format!.toLowerCase()}`}>
            {header?.mediaUrl && /^https?:\/\//i.test(header.mediaUrl) && header.format === 'IMAGE'
              ? <img src={header.mediaUrl} alt="" />
              : <span className="rr-wa-preview__media-icon" aria-hidden>{MEDIA_GLYPH[header!.format!] ?? '📎'}</span>}
          </div>
        ) : header?.text ? (
          <div className="rr-wa-preview__header">
            {render(header.text)}
          </div>
        ) : null}

        <div className="rr-wa-preview__body">
          {body ? render(body) : <span className="rr-wa-preview__empty">{emptyBodyLabel}</span>}
        </div>

        {footer && <div className="rr-wa-preview__footer">{render(footer)}</div>}

        {timestamp && <div className="rr-wa-preview__time">{timestamp}</div>}

        {/* The tail is what makes it read as a message rather than a card. */}
        <span className="rr-wa-preview__tail" aria-hidden />
      </div>

      {buttons && buttons.length > 0 && (
        <div className="rr-wa-preview__buttons">
          {buttons.map((b, i) => (
            <div key={i} className="rr-wa-preview__button">{b.text}</div>
          ))}
        </div>
      )}
    </>
  );

  if (!chrome) {
    return (
      <div className={['rr-wa-preview', className].filter(Boolean).join(' ')}>
        {message}
      </div>
    );
  }

  return (
    <div className={['rr-wa-phone', className].filter(Boolean).join(' ')}>
      <div className="rr-wa-phone__bar">
        <span className="rr-wa-phone__back" aria-hidden>‹</span>
        <span className="rr-wa-phone__avatar" aria-hidden>
          {typeof businessName === 'string' ? businessName.charAt(0).toUpperCase() : 'R'}
        </span>
        <span className="rr-wa-phone__name">
          {businessName}
          <VerifiedTick />
        </span>
        <span className="rr-wa-phone__menu" aria-hidden>⋮</span>
      </div>

      <div className="rr-wa-phone__canvas">
        <div className="rr-wa-preview">{message}</div>
      </div>

      {/* Deliberately inert: it is scenery that tells the eye this is a chat,
          and a working-looking input that does nothing is worse than none. */}
      <div className="rr-wa-phone__composer" aria-hidden>
        <span className="rr-wa-phone__composer-field">{composerLabel}</span>
        <span className="rr-wa-phone__mic">🎙</span>
      </div>
    </div>
  );
}

function isMedia(format: string | undefined): boolean {
  return format === 'IMAGE' || format === 'VIDEO' || format === 'DOCUMENT';
}

/** Stand-ins for a media header, when there is no sample to show. */
const MEDIA_GLYPH: Record<string, string> = { IMAGE: '🖼', VIDEO: '🎬', DOCUMENT: '📄' };

/** The blue tick beside a verified business name. */
function VerifiedTick() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden className="rr-wa-phone__verified">
      <circle cx="8" cy="8" r="7" fill="currentColor" />
      <polyline points="4.8,8.2 7,10.3 11.2,5.8" fill="none" stroke="#fff" strokeWidth="1.7"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
